import React, { useState } from 'react';
import { Sparkles, Cpu, Users, Building2, Play, DollarSign, Calendar, Phone, CreditCard, Linkedin } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import BookingPopup from '@/components/BookingPopup';
import { preloadRoute } from '@/hooks/useRoutePreload';

const PlatformMegaMenu: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const bgColor = isDark ? '#1a1a1a' : '#f4f2f1';
  const hoverBg = isDark ? '#2a2a2a' : '#F5F5F2';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const labelColor = isDark ? '#888888' : '#888888';
  const titleColor = isDark ? '#f4f2f1' : '#111111';
  const descColor = isDark ? '#999999' : '#555555';
  const iconColor = isDark ? '#888888' : '#888888';
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  return (
    <>
      <div className="w-[650px] flex" style={{ backgroundColor: bgColor }}>
        {/* Main Content Area */}
        <div className="flex-1 p-6">
          {/* PLATFORM Category */}
          <div className="mb-6">
            <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
              Platform
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <a href="/features" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/features'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <Sparkles className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-sm font-medium" style={{ color: titleColor }}>Features</h4>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>Full platform overview</p>
                </div>
              </a>
              <a href="/ai" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/ai'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <Cpu className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>AI</h4>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: isDark ? '#f4f2f1' : '#111111', color: isDark ? '#111111' : '#f4f2f1' }}>
                      24/7
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>AI-powered automation</p>
                </div>
              </a>
            </div>
          </div>

          {/* SERVICES Category */}
          <div className="mb-6">
            <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
              Services
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <a href="/done-for-you" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/done-for-you'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <Users className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-sm font-medium" style={{ color: titleColor }}>Done-For-You</h4>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>Full-service marketing</p>
                </div>
              </a>
              <a href="/linkedin-outreach" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/linkedin-outreach'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <Linkedin className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>LinkedIn Outreach</h4>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#0d7680', color: 'white' }}>
                      New
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>AI for financial advisors</p>
                </div>
              </a>
            </div>
          </div>

          {/* EXPLORE Category */}
          <div>
            <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
              Explore
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <a href="/platform-demos" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/platform-demos'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <Play className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-sm font-medium" style={{ color: titleColor }}>Platform Demos</h4>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>See it in action</p>
                </div>
              </a>
              <a href="/pricing" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/pricing'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                  <DollarSign className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Pricing</h4>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#0d7680', color: 'white' }}>
                      Free Trial
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: descColor }}>Plans & pricing</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-[180px] p-6 border-l" style={{ borderColor, backgroundColor: isDark ? '#222222' : '#FFFFFF' }}>
          <h3 className="text-[11px] font-medium uppercase tracking-wider mb-4" style={{ color: labelColor }}>
            Get Started
          </h3>
          <div className="space-y-3">
            <button 
              onClick={() => setIsBookingOpen(true)}
              className="group flex items-center gap-2 text-sm font-medium transition-colors hover:underline cursor-pointer" 
              style={{ color: titleColor }}
            >
              <Calendar className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              Book a Demo
            </button>
            <a href="/pricing" className="group flex items-center gap-2 text-sm font-medium transition-colors hover:underline" style={{ color: titleColor }} onMouseEnter={() => preloadRoute('/pricing')}>
              <CreditCard className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              View Pricing
            </a>
          </div>
        </div>
      </div>

      <BookingPopup isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </>
  );
};

export default PlatformMegaMenu;
